/* Chillab Shoppable Video — shared behaviour for the section and the product-info block.
   Tolstoy-style UX, app-free:
   · Inline cards autoplay MUTED previews while in the viewport (IntersectionObserver).
   · Clicking a card opens an expanded player: fullscreen on mobile, a bottom-right docked
     vertical panel on desktop — with sound, a vertical scroll-snap feed (swipe / wheel /
     chevrons / arrow keys to change video), sound toggle, and a product card with
     Add to cart. The add button carries .cart-upsell__add so the global handler in
     cart-recommendations.js does the cart add + opens the cart drawer; the overlay
     closes itself so the drawer is visible.
   Idempotent: safe to load more than once; re-inits on theme-editor events. */
(function () {
  if (window.__svidLoaded) return;
  window.__svidLoaded = true;

  var SCALE = 0.78;
  var REDUCED_MOTION = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- Analytics ----------------
     Triple-report (Shopify Analytics + dataLayer + GA4) now lives in the
     shared assets/chillab-analytics.js so other sections (e.g. the homepage
     SKU grid) can reuse it instead of forking their own copy. This just
     delegates to it, keeping the local `track(name, data)` call sites below
     unchanged. */
  function track(name, data) {
    if (window.ChillabAnalytics && typeof window.ChillabAnalytics.track === 'function') {
      window.ChillabAnalytics.track(name, data);
    }
  }

  /* ---------------- Inline carousels: arrows ---------------- */

  function updateArrows(root) {
    if (root.svidFocus) { root.svidFocus.updateArrows(); return; }
    var track = root.querySelector('[data-svid-track]');
    if (!track) return;
    var prev = root.querySelector('[data-svid-prev]');
    var next = root.querySelector('[data-svid-next]');
    var maxScroll = track.scrollWidth - track.clientWidth - 2;
    if (prev) prev.hidden = track.scrollLeft <= 2;
    if (next) next.hidden = track.scrollLeft >= maxScroll || maxScroll <= 0;
  }

  function initTrack(root) {
    if (!root || root.dataset.svidReady) return;
    root.dataset.svidReady = '1';
    if (root.classList.contains('svid--focus')) {
      initFocus(root);
      root.querySelectorAll('.svid-media__video').forEach(observePreview);
      return;
    }
    var track = root.querySelector('[data-svid-track]');
    var prev = root.querySelector('[data-svid-prev]');
    var next = root.querySelector('[data-svid-next]');
    function step() {
      var card = track && track.querySelector('.svid-card');
      return card ? card.getBoundingClientRect().width + 14 : 300;
    }
    if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
    if (track) { track.addEventListener('scroll', function () { updateArrows(root); }, { passive: true }); }
    // Centered layout on mobile: open on the middle card so both sides show a peek
    // (instead of starting on card 1 with an empty left gutter).
    if (track && root.classList.contains('svid--centered') && window.matchMedia('(max-width: 749px)').matches) {
      var cc = track.querySelectorAll('.svid-card');
      if (cc.length > 1) {
        var mid = cc[Math.floor(cc.length / 2)];
        track.scrollLeft = mid.offsetLeft - (track.clientWidth - mid.offsetWidth) / 2;
      }
    }
    updateArrows(root);
    root.querySelectorAll('.svid-media__video').forEach(observePreview);
  }

  /* Centre focus: snap positions never depend on the highlighted card's size.
     Native touch/trackpad scrolling and arrows share the same centre calculation. */
  function initFocus(root) {
    var rail = root.querySelector('[data-svid-track]');
    var cards = rail ? Array.from(rail.querySelectorAll('.svid-card')) : [];
    if (!cards.length) return;
    var prev = root.querySelector('[data-svid-prev]');
    var next = root.querySelector('[data-svid-next]');
    var active = Math.floor(cards.length / 2);
    var target = null;
    var frame = 0;
    var settleTimer;

    function distance(card) {
      var r = rail.getBoundingClientRect();
      var c = card.getBoundingClientRect();
      return c.left + c.width / 2 - r.left - rail.clientWidth / 2;
    }
    function nearest() {
      var best = 0;
      cards.forEach(function (card, i) {
        if (Math.abs(distance(card)) < Math.abs(distance(cards[best]))) best = i;
      });
      return best;
    }
    function arrows() {
      var index = target === null ? active : target;
      if (prev) { prev.hidden = false; prev.disabled = index === 0; }
      if (next) { next.hidden = false; next.disabled = index === cards.length - 1; }
    }
    function size() {
      var w = cards[0].getBoundingClientRect().width;
      if (!(w > 0)) return;
      var base = w * 16 / 9;
      cards.forEach(function (card, index) {
        var media = card.querySelector('.svid-media');
        if (!media) return;
        var h = Math.round(base * (index === active ? 1 : SCALE));
        var height = h + 'px';
        var padding = Math.round((base - h) / 2) + 'px';
        if (media.style.height !== height) media.style.height = height;
        if (card.style.paddingBlock !== padding) card.style.paddingBlock = padding;
      });
    }
    function mark(index) {
      active = index;
      cards.forEach(function (card, i) {
        card.classList.toggle('svid-card--active', i === index);
      });
      arrows();
      size();
    }
    function select(index, instant) {
      target = Math.max(0, Math.min(index, cards.length - 1));
      rail.scrollTo({ left: rail.scrollLeft + distance(cards[target]),
        behavior: instant || REDUCED_MOTION ? 'instant' : 'smooth' });
      if (instant || REDUCED_MOTION) { mark(target); target = null; }
      arrows();
    }
    function settled() {
      clearTimeout(settleTimer);
      target = null;
      mark(nearest());
    }
    function scroll() {
      if (!frame) frame = requestAnimationFrame(function () { frame = 0; mark(nearest()); });
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settled, 160);
    }
    rail.addEventListener('scroll', scroll, { passive: true });
    rail.addEventListener('scrollend', settled);
    rail.addEventListener('pointerdown', function () { target = null; });
    rail.addEventListener('wheel', function () { target = null; }, { passive: true });
    if (prev) prev.addEventListener('click', function () { select((target === null ? active : target) - 1); });
    if (next) next.addEventListener('click', function () { select((target === null ? active : target) + 1); });
    rail.addEventListener('keydown', function (event) {
      var index = target === null ? active : target;
      if (event.key === 'ArrowLeft') index--;
      else if (event.key === 'ArrowRight') index++;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = cards.length - 1;
      else return;
      event.preventDefault();
      index = Math.max(0, Math.min(index, cards.length - 1));
      var media = cards[index].querySelector('.svid-media');
      if (media) media.focus({ preventScroll: true });
      select(index);
    });
    // Tabbing to an off-screen product or video brings its entire card into view.
    rail.addEventListener('focusin', function (event) {
      var index = cards.indexOf(event.target.closest('.svid-card'));
      if (index !== -1 && Math.abs(distance(cards[index])) > 2) select(index);
    });
    root.svidFocus = { updateArrows: arrows, select: select, active: function () { return active; } };
    mark(active);
    select(active, true);
    // Re-centre the chosen card after viewport/editor layout changes, never reset to the start.
    var width = rail.clientWidth;
    var resize = new ResizeObserver(function () {
      if (rail.clientWidth === width) return;
      width = rail.clientWidth;
      select(target === null ? active : target, true);
    });
    resize.observe(rail);
    root.svidFocus.destroy = function () {
      resize.disconnect();
      clearTimeout(settleTimer);
      cancelAnimationFrame(frame);
    };
  }

  function initAll() { document.querySelectorAll('.svid').forEach(initTrack); }

  // Product-chip add-to-cart: the actual add + cart-drawer open is handled by the
  // global .cart-upsell__add handler (cart-recommendations.js). Here we only fire
  // analytics, tagged source:'chip' to distinguish from player adds.
  document.addEventListener('click', function (e) {
    var chipAdd = e.target.closest && e.target.closest('.svid-chip__add');
    if (!chipAdd) return;
    track('chillab_video_add_to_cart', { source: 'chip', variant_id: chipAdd.getAttribute('data-variant-id') || '' });
  });

  /* ---------------- Inline cards: muted autoplay preview ---------------- */

  var previewIO = null;
  function observePreview(video) {
    if (REDUCED_MOTION || video.dataset.svidObserved) return;
    video.dataset.svidObserved = '1';
    video.muted = true;
    video.loop = true;
    video.controls = false;
    video.setAttribute('playsinline', '');
    if (!previewIO) {
      previewIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target;
          var media = v.closest('.svid-media');
          if (entry.intersectionRatio >= 0.5) {
            v.muted = true;
            var p = v.play();
            if (p && p.then) {
              p.then(function () { if (media) media.classList.add('is-previewing'); })
               .catch(function () { /* autoplay blocked → poster stays */ });
            }
          } else {
            v.pause();
            if (media) media.classList.remove('is-previewing');
          }
        });
      }, { threshold: [0, 0.5] });
    }
    previewIO.observe(video);
  }

  /* ---------------- Expanded player (Tolstoy-style) ---------------- */

  var overlay = null;
  var overlayScrollRAF = null;
  var lastFocus = null;

  function cardData(card) {
    var video = card.querySelector('.svid-media__video');
    if (!video) return null;
    var sources = [];
    if (video.src) sources.push({ src: video.src, type: video.getAttribute('type') || '' });
    video.querySelectorAll('source').forEach(function (s) { sources.push({ src: s.src, type: s.type }); });
    if (!sources.length) return null;
    return {
      sources: sources,
      poster: (card.querySelector('.svid-media__poster') || {}).src || video.poster || '',
      caption: card.dataset.caption || '',
      title: card.dataset.title || '',
      price: card.dataset.price || '',
      url: card.dataset.url || '',
      image: card.dataset.image || '',
      variantId: card.dataset.variantId || '',
      available: card.dataset.available === 'true',
      ctaAction: card.dataset.ctaAction || 'add',
      ctaLabel: card.dataset.ctaLabel || 'Shop now',
      soldoutLabel: card.dataset.soldoutLabel || 'Sold out'
    };
  }

  var SVG_CLOSE = '<svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><path d="M4 4l12 12M16 4L4 16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
  var SVG_UP = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M5 15l7-7 7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var SVG_DOWN = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M5 9l7 7 7-7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var SVG_SOUND_ON = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor"/><path d="M16 8a5 5 0 0 1 0 8M18.5 5.5a9 9 0 0 1 0 13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
  var SVG_SOUND_OFF = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor"/><path d="M16 9l5 6M21 9l-5 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

  function buildOverlay(cards, startIndex) {
    closeOverlay();
    lastFocus = document.activeElement;
    var items = [];
    cards.forEach(function (c) { var d = cardData(c); if (d) items.push(d); });
    if (!items.length) return;
    startIndex = Math.max(0, Math.min(startIndex, items.length - 1));

    var opened = items[startIndex] || {};
    track('chillab_video_open', {
      video_title: opened.title || '',
      product_url: opened.url || '',
      variant_id: opened.variantId || '',
      video_index: startIndex,
      video_count: items.length
    });

    overlay = document.createElement('div');
    overlay.className = 'svid-player';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Video player');

    var panel = document.createElement('div');
    panel.className = 'svid-player__panel';
    overlay.appendChild(panel);

    var feed = document.createElement('div');
    feed.className = 'svid-player__feed';
    panel.appendChild(feed);

    items.forEach(function (d, i) {
      var slide = document.createElement('div');
      slide.className = 'svid-player__slide';
      slide.dataset.index = i;

      var v = document.createElement('video');
      v.className = 'svid-player__video';
      v.setAttribute('playsinline', '');
      v.loop = true;
      v.preload = 'none';
      if (d.poster) v.poster = d.poster;
      d.sources.forEach(function (s) {
        var src = document.createElement('source');
        src.src = s.src; if (s.type) src.type = s.type;
        v.appendChild(src);
      });
      slide.appendChild(v);

      if (d.caption) {
        var cap = document.createElement('div');
        cap.className = 'svid-player__caption';
        cap.textContent = d.caption;
        slide.appendChild(cap);
      }

      if (d.title) {
        var shop = document.createElement('div');
        shop.className = 'svid-player__shop';
        var img = d.image ? '<img src="' + d.image + '" alt="" width="48" height="48" loading="lazy">' : '';
        var btn;
        if (d.ctaAction === 'add' && d.variantId && d.available) {
          btn = '<button type="button" class="svid-player__btn cart-upsell__add" data-variant-id="' + d.variantId + '">' + d.ctaLabel + '</button>';
        } else if (d.ctaAction === 'add' && d.variantId && !d.available) {
          btn = '<button type="button" class="svid-player__btn" disabled>' + d.soldoutLabel + '</button>';
        } else {
          btn = '<a href="' + d.url + '" class="svid-player__btn">' + d.ctaLabel + '</a>';
        }
        shop.innerHTML = img +
          '<span class="svid-player__shopinfo"><span class="svid-player__shoptitle">' + d.title + '</span>' +
          (d.price ? '<span class="svid-player__shopprice">' + d.price + '</span>' : '') + '</span>' + btn;
        slide.appendChild(shop);
      }
      feed.appendChild(slide);
    });

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'svid-player__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = SVG_CLOSE;
    closeBtn.addEventListener('click', closeOverlay);
    panel.appendChild(closeBtn);

    var soundBtn = document.createElement('button');
    soundBtn.type = 'button';
    soundBtn.className = 'svid-player__sound';
    soundBtn.setAttribute('aria-label', 'Toggle sound');
    soundBtn.innerHTML = SVG_SOUND_ON;
    soundBtn.addEventListener('click', function () {
      var v = activeVideo();
      if (!v) return;
      v.muted = !v.muted;
      soundBtn.innerHTML = v.muted ? SVG_SOUND_OFF : SVG_SOUND_ON;
    });
    panel.appendChild(soundBtn);

    if (items.length > 1) {
      var upBtn = document.createElement('button');
      upBtn.type = 'button';
      upBtn.className = 'svid-player__nav svid-player__nav--up';
      upBtn.setAttribute('aria-label', 'Previous video');
      upBtn.innerHTML = SVG_UP;
      upBtn.addEventListener('click', function () { feed.scrollBy({ top: -feed.clientHeight, behavior: 'smooth' }); });
      panel.appendChild(upBtn);

      var downBtn = document.createElement('button');
      downBtn.type = 'button';
      downBtn.className = 'svid-player__nav svid-player__nav--down';
      downBtn.setAttribute('aria-label', 'Next video');
      downBtn.innerHTML = SVG_DOWN;
      downBtn.addEventListener('click', function () { feed.scrollBy({ top: feed.clientHeight, behavior: 'smooth' }); });
      panel.appendChild(downBtn);
    }

    overlay.addEventListener('click', function (e) { if (e.target === overlay) closeOverlay(); });
    // Add to cart inside the player: let the global .cart-upsell__add handler run,
    // then close so the cart drawer (lower z-index) is visible.
    overlay.addEventListener('click', function (e) {
      var addBtn = e.target.closest && e.target.closest('.cart-upsell__add');
      if (!addBtn) return;
      var slide = addBtn.closest('.svid-player__slide');
      var titleEl = slide && slide.querySelector('.svid-player__shoptitle');
      track('chillab_video_add_to_cart', {
        source: 'player',
        product_title: titleEl ? titleEl.textContent : '',
        variant_id: addBtn.getAttribute('data-variant-id') || ''
      });
      setTimeout(closeOverlay, 150);
    });

    feed.addEventListener('scroll', function () {
      if (overlayScrollRAF) return;
      overlayScrollRAF = requestAnimationFrame(function () {
        overlayScrollRAF = null;
        syncActiveSlide(feed, soundBtn);
      });
    }, { passive: true });

    document.body.appendChild(overlay);
    document.documentElement.classList.add('svid-player-open');

    // jump to the tapped video, then start it with sound (user gesture)
    var target = feed.children[startIndex];
    if (target) feed.scrollTop = target.offsetTop;
    requestAnimationFrame(function () { syncActiveSlide(feed, soundBtn, true); });
    closeBtn.focus({ preventScroll: true });

    document.addEventListener('keydown', overlayKeydown);
  }

  function activeVideo() {
    return overlay ? overlay.querySelector('.svid-player__slide.is-active video') : null;
  }

  function syncActiveSlide(feed, soundBtn, first) {
    var mid = feed.scrollTop + feed.clientHeight / 2;
    var slides = feed.children;
    for (var i = 0; i < slides.length; i++) {
      var s = slides[i];
      var active = s.offsetTop <= mid && mid < s.offsetTop + s.offsetHeight;
      var v = s.querySelector('video');
      if (active && !s.classList.contains('is-active')) {
        s.classList.add('is-active');
        if (v) {
          v.preload = 'auto';
          v.muted = false;
          var p = v.play();
          if (p && p.catch) p.catch(function () {
            v.muted = true;
            v.play().catch(function () {});
            if (soundBtn) soundBtn.innerHTML = SVG_SOUND_OFF;
          });
          if (soundBtn && !first) soundBtn.innerHTML = SVG_SOUND_ON;
        }
      } else if (!active && s.classList.contains('is-active')) {
        s.classList.remove('is-active');
        if (v) v.pause();
      }
    }
  }

  function overlayKeydown(e) {
    if (!overlay) return;
    var feed = overlay.querySelector('.svid-player__feed');
    if (e.key === 'Escape') closeOverlay();
    else if (e.key === 'ArrowDown') { e.preventDefault(); feed.scrollBy({ top: feed.clientHeight, behavior: 'smooth' }); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); feed.scrollBy({ top: -feed.clientHeight, behavior: 'smooth' }); }
  }

  function closeOverlay() {
    if (!overlay) return;
    overlay.querySelectorAll('video').forEach(function (v) { v.pause(); });
    overlay.remove();
    overlay = null;
    document.documentElement.classList.remove('svid-player-open');
    document.removeEventListener('keydown', overlayKeydown);
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus({ preventScroll: true }); } catch (e) {} }
  }

  /* ---------------- Wiring ---------------- */

  // Card click → open the expanded player at that card's position.
  document.addEventListener('click', function (e) {
    var media = e.target.closest && e.target.closest('.svid-media');
    if (!media || (overlay && overlay.contains(media))) return;
    var card = media.closest('.svid-card');
    var group = media.closest('.svid');
    if (!card || !group) return;
    var cards = [].filter.call(group.querySelectorAll('.svid-card'), function (c) { return c.querySelector('.svid-media__video'); });
    var idx = cards.indexOf(card);
    if (idx === -1) return;
    e.preventDefault();
    if (group.svidFocus && (idx !== group.svidFocus.active() ||
        Math.abs(card.getBoundingClientRect().left + card.offsetWidth / 2 -
          group.querySelector('[data-svid-track]').getBoundingClientRect().left -
          group.querySelector('[data-svid-track]').clientWidth / 2) > 2)) {
      group.svidFocus.select(idx);
      return;
    }
    buildOverlay(cards, idx);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var media = e.target.closest && e.target.closest('.svid-media');
    if (media && !overlay) { e.preventDefault(); media.click(); }
  });

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', initAll);

  window.addEventListener('resize', function () { document.querySelectorAll('.svid').forEach(updateArrows); });
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:block:select', function (event) {
    initAll();
    var card = event.target.closest('.svid-card');
    var root = card && card.closest('.svid');
    if (root && root.svidFocus) root.svidFocus.select(Array.from(root.querySelectorAll('.svid-card')).indexOf(card), true);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('.svid').forEach(function (root) {
      if (root.svidFocus) root.svidFocus.destroy();
      root.querySelectorAll('.svid-media__video').forEach(function (video) {
        if (previewIO) previewIO.unobserve(video);
        video.pause();
      });
    });
  });
})();
